import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-83d295a0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "housing-availability-search__body" }
const _hoisted_2 = { class: "housing-availability-search__body-title" }
const _hoisted_3 = {
  key: 0,
  class: "housing-availability-search__availabilities-loading"
}
const _hoisted_4 = {
  key: 0,
  class: "housing-availability-search__availabilities-loading"
}
const _hoisted_5 = { class: "housing-availability-search__body-title" }

import BaseCard from '@/components/BaseCard.vue'
import SubmitButton from '@/components/public/SubmitButton.vue'
import BaseNumberIncrementer from '@/components/BaseNumberIncrementer.vue'
import DatePicker from '@/components/DatePicker.vue'
import { FORMATS, SIZES } from '@/enums/components'
import { PropType, computed, reactive } from 'vue'
import { AvailabilityPayload } from '@/models/public/order.model'
import {
  CompanyHousingDateAvailabilityRequest,
  CompanyHousingDateAvailability,
} from '@/models/public/service.model'
import { useQueryCompanyHousingDateAvailabilityList } from '@/queries/public/services'
import format from 'date-fns/format'
import { Interval } from '@/models/shared.model'
import { formatDateTime } from '@/utils/formatters'
import endOfMonth from 'date-fns/endOfMonth'
import subDays from 'date-fns/subDays'
import parseISO from 'date-fns/parseISO'
import addDays from 'date-fns/addDays'
import { serviceResultCardCommonProps } from '@/enums/public'

interface State {
  startPickerYearMonth: YearMonth
  endPickerYearMonth: YearMonth
}


import { YearMonth } from '@/models/calendar.model'
import { StringDate } from '@/models/shared.model'

export interface HousingSearchServiceBasicDetails {
  hash_id: string
  company: {
    id: number
  }
  [key: string]: any
}

export const initializePickerYearMonth = (
  date: Date | StringDate | null,
): YearMonth => {
  if (!date) {
    date = new Date()
  } else {
    // In case of StringDate
    date = new Date(date)
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
  }
}

export default /*#__PURE__*/_defineComponent({
  __name: 'HousingAvailabilitySearch',
  props: {
  modelValue: {
    type: Object as PropType<AvailabilityPayload>,
    required: true,
  },
  service: {
    type: Object as PropType<HousingSearchServiceBasicDetails>,
    required: true,
  },
  errors: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
  groupService: serviceResultCardCommonProps.groupService,
},
  emits: ['update:modelValue', 'search'],
  setup(__props) {

const props = __props





const state = reactive<State>({
  // What month is open in datepicker. We use it to fetch and show availability per date.
  startPickerYearMonth: initializePickerYearMonth(props.modelValue.check_in),
  endPickerYearMonth: initializePickerYearMonth(props.modelValue.check_out),
})

/**
 * Parses currently open END picker year month into month interval as availabiltiy endpoint expects a period.
 */
const startPickerYearMonthAsInterval = computed<Interval>(() => {
  const startOfMonth: Date = new Date(
    state.startPickerYearMonth.year,
    state.startPickerYearMonth.month,
    1,
  )
  return {
    start: startOfMonth,
    end: endOfMonth(startOfMonth),
  }
})

/**
 * Parses currently open END picker year month into month interval as availabiltiy endpoint expects a period.
 */
const endPickerYearMonthAsInterval = computed<Interval>(() => {
  const startOfMonth: Date = new Date(
    state.endPickerYearMonth.year,
    state.endPickerYearMonth.month,
    1,
  )
  return {
    start: startOfMonth,
    end: endOfMonth(startOfMonth),
  }
})

/**
 * START date picker's available dates based on availability endpoint information which is filtered and mapped to list of dates.
 */
const startAvailableDates = computed<StringDate[]>(() => {
  const availabilities = startCalendarAvailability.value || []

  const dates = availabilities
    .filter((item: CompanyHousingDateAvailability) => {
      return item.is_available
    })
    .map((item: CompanyHousingDateAvailability) => item.date)

  return dates
})

/**
 * END date picker's available dates based on availability endpoint information which is filtered and mapped to list of dates.
 *
 * For period end the date is available when the previous date is available.
 * That means given date's availability state actually represents next day's availabilty as reservation end date.
 * That is we requested availability for the day before calendar's first shown date and why we add a day here to all available dates.
 */
const endAvailableDates = computed<StringDate[]>(() => {
  const availabilities = endCalendarAvailability.value || []

  const dates = availabilities
    .filter((item: CompanyHousingDateAvailability) => {
      return item.is_available
    })
    .map((item: CompanyHousingDateAvailability) =>
      addDays(parseISO(item.date), 1),
    )
    .map((d: Date) => format(d, 'yyyy-MM-dd'))

  return dates
})

/**
 * START datepicker date should be disabled and thus not selectable if it does not have any availabilities.
 * @param date Date to check availability for
 */
const getIsStartDateDisabled = (date: Date): boolean => {
  const dateStr: StringDate = formatDateTime(date)
  return !startAvailableDates.value.includes(dateStr)
}

/**
 * END datepicker date should be disabled and thus not selectable if it does not have any availabilities.
 * @param date Date to check availability for
 */
const getIsEndDateDisabled = (date: Date): boolean => {
  const dateStr: StringDate = formatDateTime(date)
  return !endAvailableDates.value.includes(dateStr)
}

/**
 * Here we put together the expected payload for availabilities endpoint for START datepicker.
 */
const startPickerAvailabilityRequest =
  computed<CompanyHousingDateAvailabilityRequest>(() => {
    return {
      company: props.service.company.id,
      // If we don't specify only this housing it will try to load for all housings, which is unnecessary.
      services: [props.service.hash_id],
      start: format(startPickerYearMonthAsInterval.value.start, 'yyyy-MM-dd'),
      end: format(startPickerYearMonthAsInterval.value.end, 'yyyy-MM-dd'),
      group_service: props.groupService,
    }
  })

/**
 * Here we put together the expected payload for availabilities endpoint for END datepicker.
 *
 * Period end availability logic differs from period start availability.
 * For period end the date is available if the date before is available.
 * That is why we subtract a day from start, so that we would have required availability info for the first date shown in calendar.
 */
const endPickerAvailabilityRequest =
  computed<CompanyHousingDateAvailabilityRequest>(() => {
    const endStart = subDays(endPickerYearMonthAsInterval.value.start, 1)
    return {
      company: props.service.company.id,
      // If we don't specify only this housing it will try to load for all housings, which is unnecessary.
      services: [props.service.hash_id],
      start: format(endStart, 'yyyy-MM-dd'),
      end: format(endPickerYearMonthAsInterval.value.end, 'yyyy-MM-dd'),
      group_service: props.groupService,
    }
  })

/**
 * Availabilities query to show date availabilities on the fly in the START date picker.
 */
const {
  data: startCalendarAvailability,
  isFetching: isLoadingStartPickerAvailability,
} = useQueryCompanyHousingDateAvailabilityList(startPickerAvailabilityRequest)

/**
 * Availabilities query to show date availabilities on the fly in the END date picker.
 */
const {
  data: endCalendarAvailability,
  isFetching: isLoadingEndPickerAvailability,
} = useQueryCompanyHousingDateAvailabilityList(endPickerAvailabilityRequest)

const _monthYearChange =
  (field: string) =>
  ({ month, year }: { month: number; year: number }) => {
    Object.assign(state, {
      [field]: { year, month },
    })
  }
const startMonthYearChange = _monthYearChange('startPickerYearMonth')
const endMonthYearChange = _monthYearChange('endPickerYearMonth')

return (_ctx: any,_cache: any) => {
  const _component_LoadingSpinner = _resolveComponent("LoadingSpinner")!

  return (_openBlock(), _createBlock(BaseCard, {
    class: "housing-availability-search",
    small: ""
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h4", _hoisted_2, _toDisplayString(_ctx.$t('whenAreYouGoing')), 1),
        _createVNode(DatePicker, {
          "model-value": __props.modelValue.check_in,
          label: _ctx.$t('arrival'),
          name: "checkIn",
          errors: __props.errors?.start || __props.errors?.check_in,
          class: _normalizeClass(["housing-availability-search__input", {
            'housing-availability-search__input--error':
              __props.errors?.start || __props.errors?.check_in,
          }]),
          "disabled-dates": getIsStartDateDisabled,
          "month-change-on-scroll": false,
          "month-change-on-arrows": false,
          "keep-action-row": "",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = 
            (value) =>
              _ctx.$emit('update:modelValue', {
                ...__props.modelValue,
                check_in: value ? _unref(format)(value, 'yyyy-MM-dd') : null,
              })
          ),
          onUpdateMonthYear: _unref(startMonthYearChange)
        }, {
          "action-preview": _withCtx(() => [
            (_unref(isLoadingStartPickerAvailability))
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_component_LoadingSpinner),
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t('bookingForm.loadingDatePickerTimeslots')), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          "action-buttons": _withCtx(() => []),
          _: 1
        }, 8, ["model-value", "label", "errors", "class", "onUpdateMonthYear"]),
        _createVNode(DatePicker, {
          "model-value": __props.modelValue.check_out,
          label: _ctx.$t('departure'),
          name: "checkOut",
          errors: __props.errors?.end || __props.errors?.check_out,
          class: _normalizeClass(["housing-availability-search__input", {
            'housing-availability-search__input--error':
              __props.errors?.end || __props.errors?.check_out,
          }]),
          "disabled-dates": getIsEndDateDisabled,
          "month-change-on-scroll": false,
          "month-change-on-arrows": false,
          "keep-action-row": "",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = 
            (value) =>
              _ctx.$emit('update:modelValue', {
                ...__props.modelValue,
                check_out: value ? _unref(format)(value, 'yyyy-MM-dd') : null,
              })
          ),
          onUpdateMonthYear: _unref(endMonthYearChange)
        }, {
          "action-preview": _withCtx(() => [
            (_unref(isLoadingEndPickerAvailability))
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_component_LoadingSpinner),
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t('bookingForm.loadingDatePickerTimeslots')), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          "action-buttons": _withCtx(() => []),
          _: 1
        }, 8, ["model-value", "label", "errors", "class", "onUpdateMonthYear"]),
        _createElementVNode("h4", _hoisted_5, _toDisplayString(_ctx.$t('howManyAreYou')), 1),
        _createVNode(BaseNumberIncrementer, {
          label: _ctx.$t('adult'),
          amount: __props.modelValue.adults,
          "min-amount": 1,
          errors: __props.errors?.group_size,
          class: "housing-availability-search__input",
          onClickMinus: _cache[2] || (_cache[2] = ($event: any) => (
            _ctx.$emit('update:modelValue', {
              ...__props.modelValue,
              adults: __props.modelValue.adults - 1,
            })
          )),
          onClickPlus: _cache[3] || (_cache[3] = ($event: any) => (
            _ctx.$emit('update:modelValue', {
              ...__props.modelValue,
              adults: __props.modelValue.adults + 1,
            })
          ))
        }, null, 8, ["label", "amount", "errors"]),
        _createVNode(BaseNumberIncrementer, {
          label: _ctx.$t('children'),
          amount: __props.modelValue.children,
          errors: __props.errors?.children,
          class: "housing-availability-search__input",
          onClickMinus: _cache[4] || (_cache[4] = ($event: any) => (
            _ctx.$emit('update:modelValue', {
              ...__props.modelValue,
              children: __props.modelValue.children - 1,
            })
          )),
          onClickPlus: _cache[5] || (_cache[5] = ($event: any) => (
            _ctx.$emit('update:modelValue', {
              ...__props.modelValue,
              children: __props.modelValue.children + 1,
            })
          ))
        }, null, 8, ["label", "amount", "errors"]),
        _createVNode(SubmitButton, {
          label: _ctx.$t('checkAvailability'),
          format: _unref(FORMATS).PRIMARY,
          size: _unref(SIZES).LARGE,
          disabled: __props.isPreview,
          loading: __props.loading,
          class: "housing-availability-search__create",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('search')))
        }, null, 8, ["label", "format", "size", "disabled", "loading"])
      ])
    ]),
    _: 1
  }))
}
}

})